import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Link} from 'react-router';

import classNames from 'classnames';
import {autorun} from 'mobx';

import type {Role, UserExpandedCommon} from '@yourcoach/shared/api/user';

import {useI18n} from '@yourcoach/i18n';
import {currentUserStore, userExpandCommon} from '@yourcoach/shared/api/user';
import {createCollectionStore} from '@yourcoach/shared/api/utils/collectionStore';
import CloseIcon from '@yourcoach/shared/assets/icons/close.svg';
import SearchIcon from '@yourcoach/shared/assets/icons/search.svg';
import UserPlusIcon from '@yourcoach/shared/assets/icons/user-plus.svg';
import {Image} from '@yourcoach/shared/uikit/Image';
import {getFileSrc} from '@yourcoach/shared/utils/entity/file';

import AvatarPlaceholder from '@src/components/AvatarPlaceholder';
import Button from '@src/components/Button';
import Loader from '@src/components/Loader/Loader';
import {ModalStickyFooter} from '@src/components/ModalNew';
import NoResultsHeader from '@src/components/NoResultsHeader';
import TextField from '@src/components/TextField';

import styles from './SelectCoach.module.css';

export type Props = {
  canAcceptPayments?: boolean;
  onSelect?: (coaches: Coach[]) => void;
  selection?: Coach[];
};

type Coach = UserExpandedCommon;

export const SelectCoach: FC<Props> = ({
  canAcceptPayments,
  onSelect,
  selection: selectionProp,
}) => {
  const {t} = useI18n();
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState<Coach[]>(selectionProp || []);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const shouldSearch = useMemo(() => query && query.length >= 2, [query]);

  const searchTimeout = useRef(setTimeout(() => {}, 0));
  const coachesStore = useRef(
    createCollectionStore<Coach>({
      method: 'client.users.search',
      params: {
        expand: userExpandCommon,
        query: [
          ['roles', 'in', ['coach'] as Role[]],
          ['coach_status', '!=', 'suspicious'],
        ],
      },
    }),
  ).current;

  useEffect(() => {
    const dispose = autorun(() => {
      setCoaches(coachesStore.items.slice());
      setIsFetching(coachesStore.isFetching);
    });

    return () => {
      dispose();
      coachesStore.clear();

      clearTimeout(searchTimeout.current);
    };
  }, [coachesStore]);

  const onFooterButtonPress = useCallback(() => {
    onSelect?.(selection);
  }, [onSelect, selection]);

  const onQueryChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      setQuery(e.currentTarget.value);
    },
    [],
  );

  const searchCoaches = useCallback(
    (userName: string) => {
      clearTimeout(searchTimeout.current);
      setIsFetching(true);

      searchTimeout.current = setTimeout(() => {
        coachesStore
          .fetch({
            query: [
              [
                '_id',
                '!in',
                selection.concat(currentUserStore.user!).map(x => x._id),
              ],
              ['coach_title:2,name', 'fulltext', `${userName}*`],
            ],
          })
          .catch(() => {});
      }, 1000);
    },
    [coachesStore, selection],
  );

  useEffect(() => {
    clearTimeout(searchTimeout.current);

    coachesStore.clear();

    if (shouldSearch) {
      searchCoaches(query);
    } else {
      setIsFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachesStore, query, shouldSearch]);

  const onAddCoachButtonClick = useCallback(
    (coach: Coach) => {
      if (!selection.find(item => coach._id === item._id)) {
        setSelection(prev => [...prev, coach]);

        coachesStore.removeItem(coach);

        if (!coachesStore.hasItems) {
          coachesStore.clear();
          setQuery('');
        }
      }
    },
    [coachesStore, selection],
  );

  const onDeleteCoachButtonClick = useCallback(
    (coach: Coach) => {
      const index = selection.findIndex(item => item._id === coach._id);

      if (index >= 0) {
        setSelection(prev => {
          prev.splice(index, 1);

          coachesStore.addItem(coach, 0);

          return [...prev];
        });
      }
    },
    [coachesStore, selection],
  );

  return (
    <div className={classNames('SelectCoach', styles.Component)}>
      <TextField
        onChange={onQueryChange}
        placeholder={t('web-app:SelectCoach.text_input_placeholder')}
        variant="search"
      />
      {selection.length ? (
        <div className={styles.selection}>
          {selection.map(coach => (
            <div className={styles.selectedCoach} key={coach._id}>
              <div className={styles.avatar}>
                <Image
                  placeholder={<AvatarPlaceholder name={coach.name || ''} />}
                  src={getFileSrc(coach.avatar).url}
                />
              </div>
              <p className={styles.name}>{coach.name}</p>
              <Button
                className={styles.deleteButton}
                onClick={() => onDeleteCoachButtonClick(coach)}
              >
                <CloseIcon />
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <div className={styles.resultsContainer}>
        {isFetching ? (
          <div>
            <Loader />
          </div>
        ) : coaches.length ? (
          coaches.map(coach => (
            <div
              className={classNames(
                styles.foundCoach,
                canAcceptPayments && !coach.is_provider && styles.isntProvider,
              )}
              key={coach._id}
            >
              <div className={styles.leftPart}>
                <div className={styles.avatar}>
                  <Image
                    placeholder={<AvatarPlaceholder name={coach.name || ''} />}
                    src={getFileSrc(coach.avatar).url}
                  />
                </div>
                <div className={styles.nameContainer}>
                  <Link
                    className={styles.name}
                    target="_blank"
                    to={`/coaches/${coach.slug || coach._id}`}
                  >
                    {coach.name}
                  </Link>
                  {canAcceptPayments && !coach.is_provider ? (
                    <p className={styles.message}>
                      {t('web-app:SelectCoach.isnt_provider_label')}
                    </p>
                  ) : null}
                </div>
              </div>
              <Button
                className={styles.addButton}
                disabled={canAcceptPayments && !coach.is_provider}
                onClick={() => onAddCoachButtonClick(coach)}
              >
                <UserPlusIcon className={styles.addIcon} />
                {t('shared:add')}
              </Button>
            </div>
          ))
        ) : shouldSearch ? (
          <div className={styles.noResultsContainer}>
            <NoResultsHeader
              icon={SearchIcon}
              text={t('web-app:SelectCoach.no_results_label')}
            />
          </div>
        ) : null}
      </div>
      <ModalStickyFooter>
        <div className={styles.footer}>
          <Button disabled={!selection.length} onClick={onFooterButtonPress}>
            {t('shared:confirm')}
          </Button>
        </div>
      </ModalStickyFooter>
    </div>
  );
};
